import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {firstValueFrom} from "rxjs";
import {environment} from "../../environments/environment";
import {SearchResult} from "../_models/SearchResult";
import {OrdineVendita} from "../_models/OrdineVendita";
import {OrdiniVenditaStat} from "../_models/OrdiniVenditaStat";
import {MaterialeOrdine} from "../_models/MaterialeOrdine";
import {OrdiniVenditaFilter} from "../_models/OrdiniVenditaFilter";
import {OrdineVenditaGroup, OrdiniVenditaGroupRequest} from "../_models/OrdineVenditaGroup";
import {OrdiniVenditaExportRequest} from "../_models/OrdiniVenditaExportRequest";
import {ModalitaSovrascrittura, OrdineVenditaImportResult} from "../_models/OrdineVenditaImportResult";
import {AnteprimaNumerazione} from "../_models/AnteprimaNumerazione";
import {DisponibilitaNumero} from "../_models/DisponibilitaNumero";
import {ValoriColonnaResult} from "../_models/ValoriColonnaResult";

@Injectable({ providedIn: 'root' })
export class VenditaService {
  constructor(private http: HttpClient) { }

  async getOrdiniVenditaFilteredAsync(
    orgId: number,
    search: string,
    page: number,
    count: number,
    sortField: string,
    sortAsc: boolean,
    filter: OrdiniVenditaFilter = {}
  ) {
    return firstValueFrom(this.http.post<SearchResult<OrdineVendita>>(
      `${environment.localApiUrl}sale/Ordini/OrdiniVenditaFiltered?orgId=${orgId}`,
      { search, page, count, sortField, sortAsc, ...filter }
    ));
  }

  // ⚠️ `organizzazioneId`, NON `orgId`: è il nome del parametro dell'azione
  // OrdiniController.OrdineVenditaFromId, e il binding della query string va per NOME. Con `orgId`
  // il parametro restava a 0 e l'endpoint cercava l'ordine nell'organizzazione 0 — cioè in nessuna.
  // Il controller usa ENTRAMBE le convenzioni su azioni diverse (`orgId` su OrdiniVenditaFiltered,
  // `organizzazioneId` qui): va copiato il nome dell'azione che si sta chiamando, non quello della
  // variabile locale.
  async getOrdineVenditaAsync(orgId: number, idOrdine: number, idRiga) {
    return firstValueFrom(this.http.get<OrdineVendita>(`${environment.localApiUrl}sale/Ordini/OrdineVenditaFromId?organizzazioneId=${orgId}&idOrdine=${idOrdine}&idRiga=${idRiga}`));
  }

  // Issue #2981: anteprima NON vincolante del prossimo N° registrazione per (Org, Anno).
  // Sola lettura: il valore è una stima visuale e NON va inviato nel payload di create.
  async getAnteprimaNumRegistrazioneAsync(orgId: number, anno: number) {
    return firstValueFrom(this.http.get<AnteprimaNumerazione>(
      `${environment.localApiUrl}sale/Ordini/AnteprimaNumRegistrazione?organizzazioneId=${orgId}&anno=${anno}`
    ));
  }

  // Issue #3002: verifica disponibilità (sola lettura, no side-effect) di un numero/revisione per
  // (Org, Anno). bis omesso = verifica del base; bis valorizzato = verifica di quella revisione.
  async verificaDisponibilitaNumeroAsync(orgId: number, anno: number, numRegistrazione: number, bis?: number | null) {
    const bisParam = bis !== null && bis !== undefined ? `&bis=${bis}` : '';
    return firstValueFrom(this.http.get<DisponibilitaNumero>(
      `${environment.localApiUrl}sale/Ordini/VerificaDisponibilitaNumero?organizzazioneId=${orgId}&anno=${anno}&numRegistrazione=${numRegistrazione}${bisParam}`
    ));
  }

  // Issue #3002: testata dell'ordine base per la precompilazione del bis (204 No Content → null).
  async getOrdineBaseByNumeroAsync(orgId: number, anno: number, numRegistrazione: number) {
    return firstValueFrom(this.http.get<OrdineVendita | null>(
      `${environment.localApiUrl}sale/Ordini/OrdineBaseByNumero?organizzazioneId=${orgId}&anno=${anno}&numRegistrazione=${numRegistrazione}`
    ));
  }

  async createOrdineVenditaAsync(ordine: OrdineVendita) {
    return firstValueFrom(this.http.post<OrdineVendita>(`${environment.localApiUrl}sale/Ordini/CreateOrdineVendita`, ordine));
  }

  async updateOrdineVenditaAsync(ordine: OrdineVendita) {
    return firstValueFrom(this.http.post<OrdineVendita>(`${environment.localApiUrl}sale/Ordini/UpdateOrdineVendita`, ordine));
  }

  // ⚠️⚠️ STESSO DIFETTO, CONSEGUENZE PEGGIORI. Con `orgId` il parametro `organizzazioneId`
  // dell'azione restava a 0, il servizio non trovava alcuna riga e — poiché
  // `OrdiniVenditaService.DeleteOrdineVendita` tratta «non trovato» come successo
  // (`if (ordVendita is null) return true;`) — l'endpoint rispondeva 200 OK SENZA ELIMINARE NULLA.
  // L'interfaccia dichiarava un'eliminazione riuscita che non era avvenuta.
  //
  // ⚠️ Dalla feature 122 questa chiamata ha un secondo effetto: l'eliminazione riuscita notifica
  // Pianificazione, che rimuove l'attività collegata (US3). Con l'organizzazione a 0 anche quella
  // notifica cercava a vuoto, quindi l'avviso mostrato all'operatore — «l'ordine è pianificato:
  // verrà rimosso anche dalla pianifica» — era doppiamente falso.
  //
  // 📌 Correggendo il nome, questo percorso DIVENTA DAVVERO DISTRUTTIVO per la prima volta: da qui
  // in avanti elimina l'ordine e, se pianificato, la sua attività. È il comportamento che l'interfaccia
  // ha sempre promesso, ma va collaudato sapendo che prima non accadeva nulla.
  async deleteOrdineVenditaAsync(orgId: number, idOrdine: number, idRiga: number) {
    return firstValueFrom(this.http.get<number>(`${environment.localApiUrl}sale/Ordini/DeleteOrdineVendita?organizzazioneId=${orgId}&idOrdine=${idOrdine}&idRiga=${idRiga}`));
  }

  // Issue #2971: lo stat è POST con il DTO filtri della lista così la barra statistiche
  // riflette il sottoinsieme filtrato. Body vuoto ⇒ totali dell'intera organizzazione.
  async getOrdiniVenditaStatAsync(orgId: number, search?: string, filter: OrdiniVenditaFilter = {}) {
    return firstValueFrom(this.http.post<OrdiniVenditaStat>(
      `${environment.localApiUrl}sale/Ordini/GetOrdiniVenditaStat?orgId=${orgId}`,
      { search, ...filter }
    ));
  }

  // Issue #3115 — elenco valori distinti di una colonna nel contesto dei filtri correnti (stile
  // Excel). Il filtro viaggia nello spread del body; `colonna`/`searchValore` sono aggiuntivi.
  // `search: ''` è obbligatorio: SearchPaged.Search è non-nullable e con [ApiController] il model
  // binding la valida come required — senza la property la richiesta muore in 400 prima dell'action.
  async getValoriColonnaAsync(
    orgId: number,
    colonna: string,
    searchValore: string | null,
    filter: OrdiniVenditaFilter = {}
  ) {
    return firstValueFrom(this.http.post<ValoriColonnaResult>(
      `${environment.localApiUrl}sale/Ordini/OrdiniVenditaValoriColonna?orgId=${orgId}`,
      { colonna, searchValore, search: '', ...filter }
    ));
  }

  async getOrdiniVenditaGroupedAsync(orgId: number, request: OrdiniVenditaGroupRequest) {
    return firstValueFrom(this.http.post<SearchResult<OrdineVenditaGroup>>(
      `${environment.localApiUrl}sale/Ordini/OrdiniVenditaGrouped?orgId=${orgId}`,
      request
    ));
  }

  async exportOrdiniVenditaAsync(orgId: number, request: OrdiniVenditaExportRequest): Promise<Blob> {
    return firstValueFrom(this.http.post(
      `${environment.localApiUrl}sale/Ordini/ExportOrdiniVendita?orgId=${orgId}`,
      request,
      { responseType: 'blob' }
    ));
  }

  // ── Materiale di Ordine ────────────────────────────────────────────────

  async getMaterialeOfOrdineAsync(orgId: number, idOrdine: number, idRigaPadre: number) {
    return firstValueFrom(this.http.get<MaterialeOrdine[]>(
      `${environment.localApiUrl}sale/Ordini/GetMaterialeOfOrdine?orgId=${orgId}&idOrdine=${idOrdine}&idRigaPadre=${idRigaPadre}`
    ));
  }

  async addMaterialeAsync(materiale: MaterialeOrdine) {
    return firstValueFrom(this.http.post<MaterialeOrdine>(
      `${environment.localApiUrl}sale/Ordini/AddMateriale`, materiale
    ));
  }

  async updateMaterialeAsync(materiale: MaterialeOrdine) {
    return firstValueFrom(this.http.post<MaterialeOrdine>(
      `${environment.localApiUrl}sale/Ordini/UpdateMateriale`, materiale
    ));
  }

  async deleteMaterialeAsync(orgId: number, idOrdine: number, idRigaPadre: number, idRigaMateriale: number) {
    return firstValueFrom(this.http.get<{ result: string }>(
      `${environment.localApiUrl}sale/Ordini/DeleteMateriale?orgId=${orgId}&idOrdine=${idOrdine}&idRigaPadre=${idRigaPadre}&idRigaMateriale=${idRigaMateriale}`
    ));
  }

  // ── Import Ordini Clienti da file (feature 034) ────────────────────────

  analyzeOrdiniVenditaFile(orgId: number, tracciatoId: number, file: File) {
    const form = new FormData();
    form.append('file', file);
    form.append('orgId', String(orgId));
    form.append('tracciatoId', String(tracciatoId));
    return this.http.post<OrdineVenditaImportResult>(
      `${environment.localApiUrl}sale/Ordini/ImportaOrdiniVenditaAnalyze`, form);
  }

  confirmOrdiniVenditaImport(report: OrdineVenditaImportResult, modalita: ModalitaSovrascrittura = ModalitaSovrascrittura.AggiornaTutto) {
    return this.http.post<{ message: string }>(
      `${environment.localApiUrl}sale/Ordini/ImportaOrdiniVenditaConfirm`, { report, modalita });
  }
}
